import { useHover } from "@redotech/react-util/hover";
import DotsReorderIcon from "@redotech/redo-icons/dots-reorder.svg";
import FolderOpenIcon from "@redotech/redo-icons/folder-open.svg";
import FolderIcon from "@redotech/redo-icons/folder.svg";
import TriangleDown from "@redotech/redo-icons/triangle-down_filled.svg";
import classNames from "classnames";
import { memo, useEffect, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { Flex } from "../../../../flex";
import { Text } from "../../../../text";
import { OverflowTooltip } from "../../../../tooltip/overflow-tooltip";
import { RedoBadge } from "../../../badge/redo-badge";
import { RedoIcon } from "../../../icon/redo-icon";
import * as navigationCss from "../../redo-navigation.module.css";
import * as folderItemCss from "./tree-item.module.css";

export type RedoFolderItemProps = {
  friendlyName: string;
  dragHandleProps: object;
  onToggleExpanded: () => void;
  onRequestEdit: () => void;
  childrenLinks: string[];
  expanded: boolean;
  notificationCount?: number;
  clone?: boolean;
};

export const RedoFolderItem = memo(function RedoFolderItem({
  friendlyName,
  dragHandleProps,
  onToggleExpanded,
  onRequestEdit,
  childrenLinks,
  expanded,
  clone,
  notificationCount,
}: RedoFolderItemProps) {
  const [componentRef, setComponentRef] = useState<HTMLDivElement | null>(null);
  const [overflowRef, setOverflowRef] = useState<HTMLDivElement | null>(null);

  const location = useLocation();
  const matchedPath = childrenLinks.find((path) =>
    matchPath({ path }, location.pathname),
  );

  const componentHovered = useHover(componentRef);

  useEffect(() => {
    if (matchedPath && !expanded) {
      onToggleExpanded();
    }
    // I only want to run this once, on render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Flex
      as="button"
      className={classNames(navigationCss.navItem, folderItemCss.folderItem)}
      justify="space-between"
      onClick={(e) => {
        e.preventDefault();
        onToggleExpanded();
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onRequestEdit();
      }}
      px="sm"
      py="sm"
      radius="sm"
      ref={setComponentRef}
      w="full"
      {...dragHandleProps}
    >
      <Flex
        align="center"
        basis={0}
        gap="md"
        grow={1}
        overflow="hidden"
        shrink={1}
        style={{ minWidth: 0 }}
      >
        <Flex shrink={0}>
          <RedoIcon
            color="tertiary"
            Icon={expanded ? FolderOpenIcon : FolderIcon}
            size={16}
          />
        </Flex>
        <OverflowTooltip
          hideOnHover
          overflowRef={clone ? null : overflowRef}
          tooltipProps={{ title: friendlyName, darkMode: true }}
          wrapperStyle={{
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Text
            fontSize="xs"
            overflow="hidden"
            ref={setOverflowRef}
            textColor="secondary"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {friendlyName}
          </Text>
        </OverflowTooltip>
      </Flex>
      <Flex
        align="center"
        className={classNames(
          componentHovered && navigationCss.show,
          navigationCss.dragHandle,
          clone && navigationCss.clone,
        )}
        onClick={(e) => {
          e.preventDefault();
        }}
      >
        <RedoIcon color="tertiary" Icon={DotsReorderIcon} size={16} />
      </Flex>
      <Flex align="center" gap="xs">
        {notificationCount !== undefined && !expanded && (
          <RedoBadge
            color={notificationCount > 0 ? "error" : "black"}
            size="xs"
            text={notificationCount.toString()}
            type="alternate"
          />
        )}
        <RedoIcon
          className={classNames(
            navigationCss.dropdownIcon,
            expanded && navigationCss.active,
          )}
          color="tertiary"
          Icon={TriangleDown}
          size={16}
        />
      </Flex>
      <Flex
        className={classNames(
          folderItemCss.pipeContainer,
          expanded && folderItemCss.expanded,
        )}
        style={
          { "--children-count": childrenLinks.length } as React.CSSProperties
        }
      />
    </Flex>
  );
});
